// Trades table: Time/Side/Price/Fee/BE/Status/PnL with netted/filled logic.
// Mirrors legacy showDetail trades rendering exactly. Opening (unmatched,
// un-netted) rows are clickable and open the CloseTradeModal.
import { useState, useRef, useEffect } from "react";
import type { Trade, Order } from "@/types/api";
import { fmt, timeAgo, classifyFee } from "@/lib/format";
import {
  buildNettedByMap,
  sortTrades,
  tradeBreakEven,
  tradeIsRelaxedBE,
  findBeOrder,
} from "@/lib/grid-trade-pairing";
import CloseTradeModal from "@/components/grid/close-trade-modal";

const TAKER_FEE_RATE = 0.0007;

interface GridTradesTableProps {
  gridId: string;
  trades: Trade[];
  orders: Order[];
  symbol: string;
  maxHoldMinutes: number | null;
  livePrice: number | null;
}

export default function GridTradesTable({
  gridId,
  trades,
  orders,
  symbol,
  maxHoldMinutes,
  livePrice,
}: GridTradesTableProps) {
  const [hideNetted, setHideNetted] = useState(true);
  const [closeTarget, setCloseTarget] = useState<Trade | null>(null);
  // Track recent trade IDs for flash animation
  const prevIdsRef = useRef<Set<number>>(new Set());
  const [recentIds, setRecentIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const newIds = new Set<number>();
    for (const t of trades) {
      if (!prevIdsRef.current.has(t.id)) newIds.add(t.id);
    }
    if (newIds.size > 0) {
      setRecentIds(newIds);
      const timer = setTimeout(() => setRecentIds(new Set()), 2000);
      prevIdsRef.current = new Set(trades.map((t) => t.id));
      return () => clearTimeout(timer);
    }
    prevIdsRef.current = new Set(trades.map((t) => t.id));
  }, [trades]);

  const nettedBy = buildNettedByMap(trades);
  const sorted = sortTrades(trades);
  const tradeById = new Map(trades.map((t) => [t.id, t]));
  const closerByOpenerId = new Map<number, Trade>();
  for (const t of trades) {
    if (t.opening_trade_id) closerByOpenerId.set(t.opening_trade_id, t);
  }

  const visible = hideNetted ? sorted.filter((t) => !nettedBy.has(t.id)) : sorted;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted">
          Trades
        </h3>
        <button
          className={`inline-flex items-center h-7 px-3 text-xs font-medium rounded border transition-colors ${
            hideNetted
              ? "border-accent/60 text-accent bg-accent/10"
              : "border-border text-muted hover:text-text"
          }`}
          onClick={() => setHideNetted((v) => !v)}
        >
          Hide netted
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="text-muted text-xs text-center py-4">No trades</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-muted border-b border-border">
                <th className="text-left py-1 pr-2 font-normal">Time</th>
                <th className="text-left py-1 pr-2 font-normal">Side</th>
                <th className="text-right py-1 pr-2 font-normal">Price</th>
                <th className="text-right py-1 pr-2 font-normal hidden md:table-cell">Fee</th>
                <th className="text-right py-1 pr-2 font-normal hidden md:table-cell">BE</th>
                <th className="text-left py-1 pr-2 font-normal">Status</th>
                <th className="text-right py-1 font-normal">PnL</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => {
                const isNew = recentIds.has(t.id);
                const rowClass = isNew
                  ? t.side === "buy"
                    ? "bg-success/10 transition-colors"
                    : "bg-danger/10 transition-colors"
                  : "";

                // Fee cell
                const feeCell = t.fee ? (() => {
                  const c = classifyFee(t.fee, t.price, t.quantity);
                  return (
                    <span title={c.title} className="text-dim">
                      {fmt(t.fee, 4)}
                    </span>
                  );
                })() : <span className="text-muted">—</span>;

                // BE cell
                let beCell: React.ReactNode;
                if (t.matched === 0 && !nettedBy.has(t.id)) {
                  const beOrder = findBeOrder(t, orders);
                  const relaxed = tradeIsRelaxedBE(t, maxHoldMinutes);
                  const beColor = relaxed ? "text-info" : "text-accent";
                  if (beOrder) {
                    beCell = (
                      <span
                        className={beColor}
                        title={`BE order on book @ ${fmt(beOrder.price, symbol)}`}
                      >
                        {fmt(beOrder.price, symbol)}
                      </span>
                    );
                  } else {
                    const beRate = relaxed ? 0.0002 : TAKER_FEE_RATE;
                    const bePrice = tradeBreakEven(t, beRate);
                    beCell = (
                      <span
                        className={beColor}
                        title={relaxed ? "Break-even target (maker rate)" : "Break-even target (taker rate)"}
                      >
                        {fmt(bePrice, symbol)}
                      </span>
                    );
                  }
                } else {
                  beCell = <span className="text-muted">—</span>;
                }

                // Status + PnL cells
                let statusCell: React.ReactNode;
                let pnlCell: React.ReactNode;

                if (t.matched === 0) {
                  const counterpart = nettedBy.get(t.id);
                  if (counterpart) {
                    statusCell = (
                      <span className="text-muted">
                        netted ↔ {fmt(counterpart.price, symbol)}
                      </span>
                    );
                    pnlCell = <span className="text-muted">—</span>;
                  } else {
                    statusCell = <span className="text-warn">opening</span>;
                    if (livePrice && t.quantity) {
                      const dir = t.side === "buy" ? 1 : -1;
                      const closeFee = livePrice * t.quantity * TAKER_FEE_RATE;
                      const est =
                        (livePrice - t.price) * t.quantity * dir - (t.fee ?? 0) - closeFee;
                      const col = est >= 0 ? "text-success" : "text-danger";
                      pnlCell = (
                        <span
                          className={`${col} italic`}
                          title="Estimated PnL if closed at market right now, after fees"
                        >
                          {est >= 0 ? "+" : ""}
                          {fmt(est, 4)}
                        </span>
                      );
                    } else {
                      pnlCell = <span className="text-muted">—</span>;
                    }
                  }
                } else if (t.opening_trade_id || (t.pnl !== 0 && t.matched === 1)) {
                  // Closer row
                  const opener = t.opening_trade_id ? tradeById.get(t.opening_trade_id) : null;
                  const ref = opener
                    ? `← ${fmt(opener.price, symbol)}`
                    : t.close_reason === "cross-net"
                    ? "(cross-net)"
                    : "← ?";
                  const reasonTitle = [t.close_reason, t.close_type].filter(Boolean).join(" · ");
                  const col = t.pnl >= 0 ? "text-success" : "text-danger";
                  statusCell = (
                    <span className={`${col} cursor-default`} title={reasonTitle}>
                      closed {ref}
                    </span>
                  );
                  pnlCell = (
                    <span className={`${col} font-semibold`}>
                      {t.pnl >= 0 ? "+" : ""}
                      {fmt(t.pnl, 4)}
                    </span>
                  );
                } else {
                  // Opener that got paired — PnL lives on the closer row
                  const closer = closerByOpenerId.get(t.id);
                  statusCell = closer ? (
                    <span className="text-muted">paired → {fmt(closer.price, symbol)}</span>
                  ) : (
                    <span className="text-muted">paired</span>
                  );
                  pnlCell = <span className="text-muted">—</span>;
                }

                const isOpening = t.matched === 0 && !nettedBy.has(t.id);
                return (
                  <tr
                    key={t.id}
                    className={`border-b border-border/40 hover:bg-surface/50 ${rowClass} ${
                      isOpening ? "cursor-pointer" : ""
                    }`}
                    data-grid-trade-id={t.id}
                    onClick={isOpening ? () => setCloseTarget(t) : undefined}
                    title={isOpening ? "Click to close this position" : undefined}
                  >
                    <td className="py-1 pr-2 text-dim">{timeAgo(t.traded_at)}</td>
                    <td className="py-1 pr-2">
                      <span className={t.side === "buy" ? "text-success" : "text-danger"}>
                        {t.side === "buy" ? "↑" : "↓"} {t.side.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-1 pr-2 text-right text-text">{fmt(t.price, symbol)}</td>
                    <td className="py-1 pr-2 text-right hidden md:table-cell">{feeCell}</td>
                    <td className="py-1 pr-2 text-right hidden md:table-cell">{beCell}</td>
                    <td className="py-1 pr-2">{statusCell}</td>
                    <td className="py-1 text-right">{pnlCell}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {closeTarget && (
        <CloseTradeModal
          gridId={gridId}
          trade={closeTarget}
          symbol={symbol}
          livePrice={livePrice}
          onClose={() => setCloseTarget(null)}
        />
      )}
    </div>
  );
}
